import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Space } from '@goatapp/web-content-components';
import { Trans } from 'react-i18next';

import { GiftCardDetailsModal } from 'gift-cards/components/pdp/GiftCardDetailsModal';
import { useGiftCardsDispatch, useGiftCardsState } from 'gift-cards/hooks/useGiftCards';
import { GiftCardsActions } from 'gift-cards/state/giftCardsReducer';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { Link } from 'shared/components/Link';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'giftCards.components.pdp.GiftCardDetails';

interface IGiftCardDetailsProps {
  darkTheme: boolean;
  giftCardAmounts: number[];
}

export const GiftCardDetails = ({ darkTheme, giftCardAmounts }: IGiftCardDetailsProps) => {
  const { isMobile } = useWindowSize();
  const { selectedSpaceId, spaceSections, spaces } = useGiftCardsState();
  const dispatch = useGiftCardsDispatch();
  const selectedSpace = spaces[selectedSpaceId];
  const [transitionToSelection, setTransitionToSelection] = useState(false);

  const handleEdit = () => {
    setTransitionToSelection(true);
    setTimeout(() => {
      dispatch({ type: GiftCardsActions.setGiftCardSelection, payload: true });
    }, 300);
  };

  return (
    <GiftCardDetails.Wrapper>
      <GiftCardDetails.SelectedSpace slideOut={transitionToSelection}>
        <Space space={spaceSections[selectedSpaceId]} maxWidth={isMobile ? 53 : 96} />
        <GiftCardDetails.EditWrapper>
          {selectedSpace.name}
          <GiftCardDetails.Edit onClick={handleEdit}>
            {t(`${TP}.edit`, 'Edit')}
          </GiftCardDetails.Edit>
        </GiftCardDetails.EditWrapper>
      </GiftCardDetails.SelectedSpace>
      <GiftCardDetails.Body $isMobile={isMobile} fadeOut={transitionToSelection}>
        <GiftCardDetailsModal giftCardAmounts={giftCardAmounts} darkTheme={darkTheme} />
        {!isMobile && (
          <GiftCardDetails.TermsAndConditions>
            <Trans
              i18nKey={`${TP}.terms`}
              defaults="<0>By proceeding, you agree to the</0> <1>Gift Card Terms</1>"
              components={[
                <span />,
                <Link
                  href="/gift-card-terms"
                  passHref
                  prefetch={false}
                  target="_blank"
                  style={{ textDecoration: 'underline' }}
                />,
              ]}
            />
          </GiftCardDetails.TermsAndConditions>
        )}
      </GiftCardDetails.Body>
    </GiftCardDetails.Wrapper>
  );
};

GiftCardDetails.Wrapper = styled.div`
  width: 100%;
  height: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-top: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-top: 8px;
  }
`;

GiftCardDetails.SelectedSpace = styled.div<{ slideOut: boolean }>`
  display: flex;
  align-items: flex-start;
  width: auto;
  z-index: ${({ theme }) => theme.zIndex.pdpFooter};
  flex-direction: row;
  justify-content: flex-start;

  ${({ slideOut }) =>
    slideOut
      ? css`
          animation: slideOut 0.5s ease-out;
        `
      : css`
          animation: slideIn 0.5s ease-in;
        `}

  @keyframes slideIn {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @keyframes slideOut {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-left: 16px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-left: 32px;
    position: fixed;
    top: 110px;
  }
`;

GiftCardDetails.Body = styled.div<{ $isMobile: boolean; fadeOut: boolean }>`
  ${({ $isMobile }) =>
    !$isMobile &&
    css`
      position: fixed;
      bottom: 108px;
      width: 100%;
    `}

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  animation: ${({ fadeOut }) => (fadeOut ? `fadeOut 1s;` : `fadeIn 2s;`)};
`;

GiftCardDetails.EditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-left: 16px;

  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-left: 12px;
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardDetails.Edit = styled.div`
  text-decoration: underline;
  cursor: pointer;
  margin-top: 4px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardDetails.TermsAndConditions = styled.div`
  text-align: center;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
`;
